import { useQuery } from '@tanstack/react-query';
import { SideDrawer } from '../ui/SideDrawer';
import { Panel } from '../ui/Panel';
import { apiClient } from '../../lib/api';
import { useOfferLookup, usePublisherLookup, useCampaignLookup } from '../../hooks/useLookups';
import SourceHeader from './SourceHeader';
import KpiStrip from './KpiStrip';
import { PerformanceChart } from './PerformanceChart';
import { DimensionTabs } from './DimensionTabs';
import { Activity, AlertTriangle } from 'lucide-react';
import { QualityCard } from './QualityCard';
import { LinkedSourceCard } from './LinkedSourceCard';
import { RecentConversions } from './RecentConversions';

interface DetailResponse {
  publisherId: number | null;
  subId: string | null;
  range: string;
  days: number;
  totals: {
    conversionsApproved: number;
    conversionsPending: number;
    conversionsRejected: number;
    conversionsReversed: number;
    revenueApproved: number;
    revenuePending: number;
    payoutApproved: number;
    clicksTotal?: number;
    clicksBot?: number;
    clicksQuality?: number;
  };
  byCountry: Array<{ clicksTotal: number; clicksQuality: number; clicksBot: number; conversions?: number; revenue?: number; country: string | null }>;
  byOffer: Array<{ clicksTotal: number; clicksQuality: number; clicksBot: number; conversions?: number; revenue?: number; offerId: number | null }>;
  byDevice: Array<{ device: string | null; clicksTotal: number; clicksQuality: number; clicksBot: number }>;
  bySource: Array<{ source: string | null; clicksTotal: number; clicksQuality: number; clicksBot: number }>;
  byOs: Array<{ os: string | null; clicksTotal: number; clicksQuality: number; clicksBot: number }>;
  byCampaign: Array<{ campaignId: number | null; conversions: number; revenue: number }>;
  timeseries: Array<{ date: string; clicksTotal: number; clicksQuality: number; clicksBot: number; conversions: number; revenue: number }>;
}

export function SubDetailDrawer({
  publisherId,
  subIdHint,
  onClose,
  range = '30d',
  customFrom,
  customTo,
}: {
  publisherId: number | null | undefined;
  subIdHint?: string;
  onClose: () => void;
  range?: 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'custom';
  customFrom?: string;
  customTo?: string;
}): JSX.Element {
  const open = publisherId !== undefined;
  const offerLookup = useOfferLookup();
  const publisherLookup = usePublisherLookup();

  const detailKey = publisherId === null ? 'unlinked' : String(publisherId);
  const query = useQuery({
    queryKey: [
      'sub-detail',
      detailKey,
      range,
      customFrom ?? '',
      customTo ?? '',
      subIdHint ?? '',
    ],
    queryFn: () =>
      apiClient.get<DetailResponse>(`/api/v1/stats/sub/${detailKey}/detail`, {
        range,
        ...(range === 'custom' && customFrom && customTo
          ? { from: customFrom, to: customTo }
          : {}),
        ...(publisherId === null && subIdHint ? { sub_id: subIdHint } : {}),
      }),
    enabled: open,
  });

  const data = query.data;
  const subTitle =
    publisherId == null
      ? subIdHint
        ? `Unlinked · ${subIdHint}`
        : 'Unlinked Conversions'
      : publisherLookup.getName(publisherId);
  const campaignLookup = useCampaignLookup();

  // Hourly stats for today — /api/v1/stats/hourly returns {hour, conversionsApproved, revenueGross}
  const hourlyQuery = useQuery({
    queryKey: ['sub-detail-hourly', range],
    queryFn: () =>
      apiClient.get<Array<{ hour: number; conversionsApproved: number; revenueGross: number }>>(
        '/api/v1/stats/hourly',
        range === 'custom' && customFrom && customTo
          ? { range, from: customFrom, to: customTo }
          : { range },
      ),
    enabled: open && range === 'today',
  });

  const kpis = (() => {
    if (!data) return null;
    const total = data.totals.clicksTotal ?? 0;
    const quality = data.totals.clicksQuality ?? 0;
    const bots = data.totals.clicksBot ?? 0;
    const convs = data.totals.conversionsApproved;
    const revenue = data.totals.revenueApproved;
    const cr = quality > 0 ? (convs / quality) * 100 : 0;
    const epc = quality > 0 ? revenue / quality : 0;
    const botRate = total > 0 ? (bots / total) * 100 : 0;
    const payout = data.totals.payoutApproved ?? 0;
    return { total, quality, bots, convs, revenue, cr, epc, botRate, payout };
  })();

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title="sub detail"
      subtitle={subTitle}
      width="w-[60vw]"
    >
      <div className="space-y-4">
        {/* a. Header drawer: title/subtitle/ACTIVE chip (handled by SourceHeader) */}
        <SourceHeader
          publisherId={publisherId}
          subId={data?.subId ?? null}
          range={range}
          customFrom={customFrom}
          customTo={customTo}
        />

        {/* b. KPI strip 8 sel */}
        {kpis && (
          <Panel level={1} bracket={false}>
            <KpiStrip kpis={kpis} />
          </Panel>
        )}

        {/* c. PERFORMANCE · HOURLY panel */}
        <Panel level={1} bracket={true}>
          <div className="p-3">
            <h4 className="text-[10px] font-display uppercase tracking-wider text-text-secondary flex items-center gap-1 mb-3">
              performance • hourly
            </h4>
            <PerformanceChart
              daily={data?.timeseries ?? []}
              hourly={hourlyQuery.data ?? undefined}
              isToday={range === 'today'}
            />
          </div>
        </Panel>

        {/* d. DIMENSIONS panel */}
        <Panel level={1} bracket={true}>
          <div className="p-3">
            <h4 className="text-[10px] font-display uppercase tracking-wider text-text-secondary flex items-center gap-1 mb-3">
              dimensions
            </h4>
            <DimensionTabs
              byCountry={data?.byCountry ?? []}
              byOffer={data?.byOffer ?? []}
              byDevice={data?.byDevice ?? []}
              byOs={data?.byOs ?? []}
              bySource={data?.bySource ?? []}
              byCampaign={data?.byCampaign ?? []}
            />
          </div>
        </Panel>

        {/* e. Two half-panel sejajar: QUALITY | LINKED SOURCE */}
        <div className="grid grid-cols-2 gap-4">
          {/* QUALITY */}
          <Panel level={1} bracket={true}>
            <div className="p-3">
              <h4 className="text-[10px] font-display uppercase tracking-wider text-text-secondary flex items-center gap-1 mb-2">
                quality
              </h4>
              <QualityCard
                subId={data?.subId ?? null}
                range={range}
                customFrom={customFrom}
                customTo={customTo}
                clicksTotal={kpis?.total ?? undefined}
                clicksQuality={kpis?.quality ?? undefined}
                clicksBot={kpis?.bots ?? undefined}
              />
            </div>
          </Panel>

          {/* LINKED SOURCE */}
          {data?.subId && publisherId != null && (
            <Panel level={1} bracket={true}>
              <div className="p-3">
                <h4 className="text-[10px] font-display uppercase tracking-wider text-text-secondary flex items-center gap-1 mb-2">
                  linked source
                </h4>
                <LinkedSourceCard
                  publisherId={publisherId}
                  subId={data.subId}
                  payoutApproved={data.totals.payoutApproved ?? 0}
                />
              </div>
            </Panel>
          )}
        </div>

        {/* f. RECENT CONVERSIONS */}
        {data?.subId && (
          <Panel level={1} bracket={true}>
            <div className="p-3">
              <h4 className="text-[10px] font-display uppercase tracking-wider text-text-secondary flex items-center gap-1 mb-3">
                recent conversions
              </h4>
              <RecentConversions
                subId={data.subId}
                range={range}
                customFrom={customFrom}
                customTo={customTo}
              />
            </div>
          </Panel>
        )}
      </div>
    </SideDrawer>
  );
}
